import React, { useState, useEffect, useRef } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const reviews = [
  {
    id: 1,
    name: "Benjamin Deppisch-Pinter",
    rating: 5,
    date: "vor 2 Jahren",
    text: "Einer der letzten kleinen Bäckereien in Wien mit herrlich leckeren Mehlspeisen und herzhaften Kuchen. Hier schmeckt man, dass noch mit Herzblut gebacken wird. Tipp: Ab 17:00 Uhr an Werktagen und ab 11:30 Uhr an Samstagen gibt es auf sämtliches Gebäck 50% Rabatt!",
    isLocalGuide: true,
    images: []
  },
  {
    id: 2,
    name: "Enzo Miniaci",
    rating: 5,
    date: "vor 3 Jahren",
    text: "Wunderbare Frühstück! Viele Süssigkeiten und weihnachtliche Kuchen, es war schwierig zu entscheiden aber alles war sehr gut. Die Frau hat uns auch Vanillekipferl geschenkt, sehr nett!",
    isLocalGuide: true,
    images: [
      "https://customer-assets.emergentagent.com/job_7e52a33b-4c46-4bf0-be21-c264c50abfa9/artifacts/r5n1bjyk_image.png",
      "https://customer-assets.emergentagent.com/job_7e52a33b-4c46-4bf0-be21-c264c50abfa9/artifacts/8hyb69xs_image.png"
    ]
  },
  {
    id: 3,
    name: "sarah h",
    rating: 5,
    date: "vor 2 Jahren",
    text: "Tolle kleine Wiener Bäckerei & Konditorei. Geheimtip! Empfehlenswert: Nussbrot, Tirolerbrot, Bierspitz, RoggenPur, Veganer Gemüse-Burger, Ribislspitz und Punschkrapfen – und natürlich die Marillenkrapfen!",
    isLocalGuide: true,
    images: []
  },
  {
    id: 4,
    name: "Christine Iastufka",
    rating: 5,
    date: "vor 3 Jahren",
    text: "Wir haben schöne Torte bestellt! Einfach köstlich! Die Bedienung auch sehr nett. Das ist eine kleine Konditorei, aber so richtig persönlich. Familienbetrieb glaube ich. Komme gerne wieder!",
    isLocalGuide: true,
    images: []
  },
  {
    id: 5,
    name: "Robert Rothschedl",
    rating: 5,
    date: "vor 6 Jahren",
    text: "Wir kaufen immer unser Brot dort. Sehr gute Auswahl an Sauerteig- und Vollkornbroten, auch das Meterbrot ist spitze. Eine kleine Bäckerei muss man außerdem unterstützen!! Emine ist unsere Lieblingsverkäuferin..",
    isLocalGuide: true,
    images: [
      "https://customer-assets.emergentagent.com/job_7e52a33b-4c46-4bf0-be21-c264c50abfa9/artifacts/alvvboti_image.png"
    ]
  },
  {
    id: 6,
    name: "Benjamin Müller",
    rating: 5,
    date: "vor 6 Jahren",
    text: "Sehr gute Bäckerei mit tollem Brot und Gebäck. (Nicht vergleichbar mit dem aus dem Supermarkt.) Sehr nettes Personal, manchmal bekommt mein Sohn ein Keks geschenkt :-)",
    isLocalGuide: true,
    images: []
  },
  {
    id: 7,
    name: "J U",
    rating: 5,
    date: "vor 5 Jahren",
    text: "Spitzen Brot und Gebäck! Wir lieben besonders das Waldviertler. Sehr freundliche Bedienung. Und danke für die vielen Keksis, die unsere Kinder schon abgestaubt haben!",
    isLocalGuide: true,
    images: []
  }
];

const StarRating = ({ rating }) => {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, index) => (
        <Star
          key={index}
          className={`w-4 h-4 ${
            index < rating
              ? 'fill-[#fad24b] text-[#fad24b]'
              : 'fill-gray-300 text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

export const ReviewsSection = () => {
  const overallRating = 4.6;
  const totalReviews = 221;
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-scroll effect
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId;
    let scrollPosition = 0;
    const scrollSpeed = 0.5; // pixels per frame

    const scroll = () => {
      if (!isPaused && scrollContainer) {
        scrollPosition += scrollSpeed;
        
        // Reset when reaching the end (half of total width for seamless loop)
        const maxScroll = scrollContainer.scrollWidth / 2;
        if (scrollPosition >= maxScroll) {
          scrollPosition = 0;
        }
        
        scrollContainer.scrollLeft = scrollPosition;
      }
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isPaused]);

  // Duplicate reviews for seamless infinite scroll
  const duplicatedReviews = [...reviews, ...reviews];

  return (
    <section id="bewertungen" className="py-16 sm:py-24 lg:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <span className="inline-block text-[#61525a] font-medium text-xs sm:text-sm tracking-wider uppercase mb-3 sm:mb-4">
            Kundenstimmen
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e1919] mb-4 sm:mb-6">
            Das sagen unsere Kunden
          </h2>
          
          {/* Overall Rating */}
          <div className="inline-flex flex-col items-center bg-[#f7f5f2] rounded-xl sm:rounded-2xl p-5 sm:p-8 mt-4">
            <div className="flex items-center gap-3 sm:gap-4 mb-2">
              <span className="text-4xl sm:text-5xl font-bold text-[#1e1919]">{overallRating}</span>
              <div className="flex flex-col items-start">
                <div className="flex gap-0.5 sm:gap-1">
                  {[...Array(5)].map((_, index) => (
                    <Star
                      key={index}
                      className={`w-5 sm:w-6 h-5 sm:h-6 ${
                        index < Math.floor(overallRating)
                          ? 'fill-[#fad24b] text-[#fad24b]'
                          : index < overallRating
                          ? 'fill-[#fad24b]/50 text-[#fad24b]'
                          : 'fill-gray-300 text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-[#736c64] text-xs sm:text-sm mt-1">{totalReviews} Bewertungen auf Google</span>
              </div>
            </div>
            <a
              href="https://maps.app.goo.gl/spTRAUnRWC8BRPht8"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#61525a] hover:text-[#4a3f45] transition-colors duration-300 mt-2"
            >
              <svg className="w-4 sm:w-5 h-4 sm:h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-xs sm:text-sm font-medium">Auf Google ansehen</span>
            </a>
          </div>
        </div>
      </div>

      {/* Carousel Container - Full Width */}
      <div 
        className="relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        {/* Gradient Overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-8 sm:w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-8 sm:w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

        {/* Scrolling Container */}
        <div 
          ref={scrollRef}
          className="flex gap-4 sm:gap-6 overflow-x-hidden py-4 px-4 sm:px-8"
          style={{ scrollBehavior: 'auto' }}
        >
          {duplicatedReviews.map((review, index) => (
            <div
              key={`${review.id}-${index}`}
              className="flex-shrink-0 w-[300px] sm:w-[380px] bg-[#f7f5f2] rounded-xl sm:rounded-2xl p-4 sm:p-6 relative group hover:shadow-xl transition-all duration-300"
            >
              {/* Quote Icon */}
              <div className="absolute top-3 sm:top-4 right-3 sm:right-4 text-[#61525a]/10">
                <Quote className="w-8 sm:w-10 h-8 sm:h-10" />
              </div>

              {/* Header */}
              <div className="flex items-start gap-2.5 sm:gap-3 mb-2.5 sm:mb-3">
                <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-full bg-[#61525a] flex items-center justify-center text-white font-bold text-xs sm:text-sm flex-shrink-0">
                  {review.name.charAt(0)}
                </div>
                <div>
                  <h4 className="font-bold text-[#1e1919] text-xs sm:text-sm">{review.name}</h4>
                  {review.isLocalGuide && (
                    <span className="text-[10px] sm:text-xs text-[#736c64]">Local Guide</span>
                  )}
                </div>
              </div>

              {/* Rating & Date */}
              <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
                <StarRating rating={review.rating} />
                <span className="text-[10px] sm:text-xs text-[#736c64]">{review.date}</span>
              </div>

              {/* Review Text */}
              <p className="text-[#1e1919] text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 line-clamp-4">
                "{review.text}"
              </p>

              {/* Review Images */}
              {review.images && review.images.length > 0 && (
                <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                  {review.images.map((image, imgIndex) => (
                    <div
                      key={imgIndex}
                      className="w-11 sm:w-14 h-11 sm:h-14 rounded-lg overflow-hidden shadow-md"
                    >
                      <img
                        src={image}
                        alt={`Kundenfoto von ${review.name}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center mt-8 sm:mt-12 px-4">
        <a
          href="https://maps.app.goo.gl/spTRAUnRWC8BRPht8"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#61525a] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-medium transition-all duration-300 hover:bg-[#4a3f45] hover:shadow-lg hover:-translate-y-0.5"
        >
          Alle {totalReviews} Bewertungen lesen
        </a>
      </div>
    </section>
  );
};

export default ReviewsSection;
